import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { Album, AlbumIdInput, CreateAlbumInput, UpdateAlbumInput } from './albums.types'

export interface AlbumOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one album; their Laravel routes require its id. */
export interface AlbumScope extends AlbumOrganizationScope {
  albumId: AlbumIdInput
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface AlbumShowKeyEntries extends AlbumOrganizationScope {
  albumId?: AlbumIdInput
}

/** Query functions read only the key. */
type AlbumQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

export async function getAlbums({
  queryKey: [{ organizationId }],
}: AlbumQueryContext<AlbumOrganizationScope>) {
  return (await axios.get<Album[]>('/albums', buildAdminRequestConfig({ organizationId }))) || []
}

export async function getAlbum({
  queryKey: [{ organizationId, albumId }],
}: AlbumQueryContext<AlbumShowKeyEntries>) {
  return (
    (await axios.get<Album>(`/albums/${albumId}`, buildAdminRequestConfig({ organizationId }))) ||
    {}
  )
}

export async function createAlbum({
  organizationId,
  ...values
}: CreateAlbumInput & AlbumOrganizationScope) {
  return axios.post<Album>(
    '/albums',
    {
      ...values,
      description: values.description ?? null,
      is_enabled: values.is_enabled ?? true,
      ...(typeof values.attach_to_default_gallery === 'boolean'
        ? { attach_to_default_gallery: values.attach_to_default_gallery }
        : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

/** Sends only the fields in `values`, so the album keeps the ones left out. */
export async function updateAlbum({
  organizationId,
  albumId,
  values,
}: AlbumScope & { values: UpdateAlbumInput }) {
  return axios.put<Album>(`/albums/${albumId}`, values, buildAdminRequestConfig({ organizationId }))
}

/** 204 No Content: resolves to the empty body. */
export async function deleteAlbum({ organizationId, albumId }: AlbumScope) {
  return axios.delete<string>(`/albums/${albumId}`, buildAdminRequestConfig({ organizationId }))
}
